import React, { ReactNode, useCallback, useMemo } from "react";
import { ControlLabel, FormControlProps, FormGroup, InputGroup } from "react-bootstrap";
import { useIntl } from "react-intl";
import { LOCALE_LABELS } from "checkout/ts/locale/labels";
import classnames from "classnames";
import { Countries } from "common/locale/constants";
import { omit, pick } from "lodash";
import { generate } from "randomstring";
import { BaseFieldProps, Field, WrappedFieldProps } from "redux-form";
import { PhoneNumber } from "univapay-node";

import { ErrorMessages } from "./components/ErrorMessages";
import { IntlTelInput } from "./components/IntlTelInput";
import { RequiredIndicator } from "./components/RequiredIndicator";
import { formatAddons, TextInputAddons } from "./utils/addons";
import { INPUT_GROUP_STYLES, INPUT_OMIT_STYLES } from "./utils/styles";

type CustomProps = {
    addons?: ReactNode | TextInputAddons;
    showErrors?: boolean;
    validationLabel: string;
    isDark?: boolean;
    isHorizontal?: boolean;

    labelStyle?: Record<string, string>;
    errorStyle?: Record<string, string>;
    invalidStyle?: Record<string, string>;
    focusStyle?: Record<string, string>;
    inputStyle?: Record<string, string>;

    showPlaceholder?: boolean;
    initialValue?: string;
    className?: string;
    forcedCountryCode?: string;

    fieldError?: string;
    currentPhoneNumber?: PhoneNumber;
};

export type PhoneNumberProps = CustomProps & FormControlProps;

export const PhoneNumberInput = ({
    addons,
    input,
    meta: { active, touched, invalid, submitting, error, initial },
    showErrors = true,
    disabled,
    label,
    required,
    labelStyle,
    errorStyle,
    invalidStyle,
    focusStyle,
    inputStyle,
    style,
    width,
    validationLabel = label,
    tabIndex,
    isDark,
    isHorizontal,
    className,
    forcedCountryCode = null,
    fieldError,
}: PhoneNumberProps & WrappedFieldProps) => {
    const { formatMessage } = useIntl();

    const { before, after, header: headerAddon } = formatAddons(addons);

    const isInvalid = (invalid || error) && touched;

    const currentFocusStyle = active ? focusStyle : {};
    const fieldStyle =
        isInvalid || fieldError
            ? { width, ...inputStyle, ...invalidStyle, ...currentFocusStyle }
            : { width, ...inputStyle, ...currentFocusStyle };

    const isFullWidth = !fieldStyle.width;

    const i18n = useMemo(
        () => ({
            ...Object.values(Countries).reduce(
                (acc, { iso, name }) => ({ ...acc, [iso]: formatMessage({ id: name }) }),
                {}
            ),

            searchPlaceholder: formatMessage({ id: LOCALE_LABELS.COMMON_INPUT_SELECT_PLACEHOLDER }),
        }),
        []
    );

    const handleChange = useCallback((value: PhoneNumber) => {
        input.onChange(value);
    }, []);

    return (
        <FormGroup
            controlId={generate()}
            className={classnames({
                "keep-color-on-error": showErrors && (isInvalid || fieldError),
                "dark-theme": isDark,
                "row": isHorizontal,
            })}
            validationState={isInvalid || fieldError ? "error" : null}
            bsSize="small"
            style={{ ...style }}>
            {label && (
                <div className={classnames({ "col-xs-3 col-sm-2 inline-horizontal-content": isHorizontal })}>
                    <ControlLabel style={{ ...labelStyle }} className={classnames({ "full-width": isFullWidth })}>
                        {label}

                        {headerAddon && <span className="header-addon">{headerAddon}</span>}

                        <RequiredIndicator required={required} />
                    </ControlLabel>
                </div>
            )}

            <div
                className={classnames("phone-number-input", {
                    "col-xs-9 col-sm-10 inline-horizontal-content": isHorizontal && label,
                    "forced-country": forcedCountryCode,
                })}>
                <InputGroup className={active ? "input-group-focus" : ""} style={pick(fieldStyle, INPUT_GROUP_STYLES)}>
                    {before && <InputGroup.Addon>{before}</InputGroup.Addon>}

                    <IntlTelInput
                        defaultValue={initial}
                        value={input.value}
                        onChange={handleChange}
                        initOptions={{ showSelectedDialCode: true, nationalMode: false, i18n }}
                        disabled={disabled || submitting}
                        tabIndex={tabIndex}
                        style={omit(fieldStyle, INPUT_OMIT_STYLES)}
                        className={className}
                        forcedCountryCode={forcedCountryCode}
                        /**
                         * Prevent passing through value and initialValue to IntlTelInput component's input
                         * html element, in order for it to remain an uncontrolled component.
                         * Updates in this child component are supposed to be handled by event listeners and refs.
                         */
                        inputProps={{
                            ...omit(input, "value", "defaultValue", "onChange"),
                            onBlur: () => input.onBlur(input.value),
                        }}
                    />

                    {after && <InputGroup.Addon>{after}</InputGroup.Addon>}
                </InputGroup>

                {showErrors && (isInvalid || fieldError) && (
                    <ErrorMessages
                        fieldLabel={validationLabel}
                        errors={fieldError || error?.localNumber || error}
                        style={{ ...errorStyle }}
                    />
                )}
            </div>
        </FormGroup>
    );
};

type FieldProps = BaseFieldProps<PhoneNumberProps>;
type PhoneNumberFieldProps = Partial<Omit<Omit<PhoneNumberProps, "ref">, keyof FieldProps>> & FieldProps;

export const PhoneNumberField = (props: PhoneNumberFieldProps) => <Field component={PhoneNumberInput} {...props} />;
